// Hero.jsx
import React, { useEffect, useRef, useState } from "react";
import "./Hero.css";
import AnalogClock from "./AnalogClock";

/* ===== Title animation setup ===== */
const LINE1 = ["Transforming"];
const LINE2 = ["Ideas", "into"];
const LINE3 = ["Digital", "Brilliance"];
const ALL_WORDS = [...LINE1, ...LINE2, ...LINE3];

function buildChars() {
  const chars = [];
  let globalIndex = 0;

  ALL_WORDS.forEach((word, wordIndex) => {
    word.split("").forEach((ch, charIndex) => {
      chars.push({ ch, wordIndex, charIndex, globalIndex: globalIndex++ });
    });

    if (wordIndex < ALL_WORDS.length - 1) {
      chars.push({
        ch: " ",
        wordIndex,
        charIndex: word.length,
        globalIndex: globalIndex++,
      });
    }
  });

  return chars;
}

const ALL_CHARS = buildChars();

export default function Hero() {
  const titleRef = useRef(null);
  const [visible, setVisible] = useState(0);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const CHAR_DELAY = 65;
    const WORD_GAP_DELAY = 135;
    const LINE_GAP_DELAY = 260;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          let i = 0;

          const tick = () => {
            if (i >= ALL_CHARS.length) return;

            const prev = ALL_CHARS[i - 1];
            const curr = ALL_CHARS[i];

            let delay = CHAR_DELAY;

            if (prev && prev.wordIndex !== curr.wordIndex) {
              const isLineBreak = prev.wordIndex === 0 || prev.wordIndex === 2;
              delay = isLineBreak
                ? LINE_GAP_DELAY + CHAR_DELAY
                : WORD_GAP_DELAY + CHAR_DELAY;
            }

            setTimeout(() => {
              setVisible(i + 1);
              i++;
              tick();
            }, delay);
          };

          tick();
          observer.unobserve(el);
        });
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const renderWord = (wordIndex, accent = false) => (
    <span className={`reveal-word ${accent ? "accent" : ""}`}>
      {ALL_CHARS.filter((c) => c.wordIndex === wordIndex).map((c) => (
        <span
          key={c.globalIndex}
          className={`reveal-char ${c.globalIndex < visible ? "char-visible" : ""}`}
        >
          {c.ch}
        </span>
      ))}
    </span>
  );

  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (!target) return;

    const nav = document.querySelector("nav");
    const navHeight = nav ? nav.offsetHeight : 0;

    const y = target.getBoundingClientRect().top + window.pageYOffset - (navHeight + 10);
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <section className="hero3d">
      <div className="hero-overlay">
        <div className="hero-container">
          <div className="hero-content-wrapper">
            {/* LEFT */}
            <div className="content">
              <h1 className="reveal-title" ref={titleRef}>
                <span className="reveal-line">{renderWord(0)}</span>

                <span className="reveal-line">
                  {renderWord(1, true)}
                  {renderWord(2)}
                </span>

                <span className="reveal-line">
                  {renderWord(3, true)}
                  {renderWord(4, true)}
                </span>
              </h1>

              <p className="description">
                We craft websites that are visually striking and engineered for seamless
                experiences turning attention into action for your brand.
              </p>

              <div className="hero-actions">
                <button className="hero-btn primary" onClick={() => scrollToSection("services")}>
                  Explore Our Services
                </button>

                <button className="hero-btn secondary" onClick={() => scrollToSection("contact")}>
                  Let&apos;s Talk
                </button>
              </div>
            </div>

            {/* RIGHT */}
            <div className="hero-clock-wrapper">
              <div className="hero-clock-inner">
                <AnalogClock />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
